// components/StationCard.tsx
import React, { memo } from "react";
import "./StationCard.css";
import { getStationDisplayProps } from "../../../../utils/getStationState";
import { formatCurrentValue } from "../../../../utils/formatCurrent";

interface StationProps {
  station: {
    id: number;
    place: string;
    state_id: number;
    status_id: number;
    currentA: number | null;
    current_dA?: number | null;
    energyWh: number | null;
    gps: string;
    last_session?: { stop_reason?: number | null } | null;
  };
  onSelect: () => void;
  role?: string;
}

/**
 * `СТ.2` (`oq-ММБ-298`, `ТЕСТ-Н111`): единый вид карточки станции.
 *
 * - класс карточки и иконка — от ключа состояния (`st-<kind>`), не от текста
 *   подписи: следующая смена слова не сломает цвет молча;
 * - шапка — одна строка «МЕСТО: N / ПОДПИСЬ», иконка в правом углу;
 * - длинное место — многоточие, полный текст в `title`;
 * - у «не готова» вместо чисел — прочерк: станция не настроена.
 */
const StationCard: React.FC<StationProps> = ({ station, onSelect }) => {
  const { place, currentA, current_dA, energyWh } = station;
  const current = formatCurrentValue(currentA, current_dA);

  const { state, kind, icon, color, hint } = getStationDisplayProps(station);
  const isEmpty = kind === "not-ready";

  return (
    <div
      role="button"
      tabIndex={0}
      className={`station-card st-${kind}`}
      onClick={onSelect}
    >
      {/* === ШАПКА === (всегда белая, одна строка) */}
      <div className="station-header">
        <h3 className="station-title">
          <span className="station-place" title={place || undefined}>
            Место: {place || "—"}
          </span>
          <span className="station-sep"> / </span>
          {/* `С2`: подсказка «под статусом» — всплывает при наведении. В карточке
              фиксированной высоты места на постоянную строку нет; та же подсказка
              показана текстом в окне станции. */}
          <span
            className="station-status"
            style={{ color }}
            title={hint ?? undefined}
            data-hint={hint ?? undefined}
          >
            {state}
          </span>
        </h3>
        <div className="station-icon" style={{ color }} aria-hidden="true">
          {icon}
        </div>
      </div>

      <div className="spaceLine" />

      {/* === ТЕЛО === (заливается цветом по состоянию) */}
      <div className="station-body">
        <div className="station-data">
          <span>Текущее потребление:</span>
          <strong className="station_value">
            {isEmpty ? "—" : current !== null ? `${current} A` : "-"}
          </strong>
        </div>
        <div className="station-data">
          <span>Показания счётчика:</span>
          <strong className="station_value">
            {isEmpty
              ? "—"
              : energyWh !== null
                ? `${energyWh / 1000} кВт·ч`
                : "-"}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default memo(StationCard, (prevProps, nextProps) => {
  const prev = prevProps.station;
  const next = nextProps.station;
  return (
    prev.id === next.id &&
    prev.status_id === next.status_id &&
    prev.state_id === next.state_id &&
    prev.currentA === next.currentA &&
    prev.current_dA === next.current_dA &&
    prev.energyWh === next.energyWh &&
    prev.place === next.place &&
    prev.gps === next.gps
  );
});
